import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTriggerAcc
} from '@/components/common/Tabs';
import { ekDomain, MY_ACCOUNT_PATH, ROUTES } from '@/configs/constants';
import { ROLE_USER } from '@/types/enums';
import { isPathEk } from '@/utils/routes';
import { UserRound } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NotFoundPage from '../not-found';
import { useEffect, useMemo, useState } from 'react';
import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem
} from '@/components/common/Carousel';
import { cn, isRoles } from '@/utils/utils';
import { useStore } from '@/hooks/useStore';
import MyAccountTab from './my-account-tab';
import RegistrationLocationTab from './registration-location-tab';
import RentalHistoryTab from './rental-history-tab';

const ENDUSER_ROLES: number[] = [
    ROLE_USER.RENTAL_BIKE_OWNER_EK,
    ROLE_USER.RENTAL_BIKE_OWNER_RD
];

export default observer(function MyAccountPage() {
    // hooks
    const { t } = useTranslation();
    const { tabPath, id } = useParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    // store
    const {
        reservationStore: { cleanRental },
        generalStore: { getMyAccount, myAccount },
        apiStore: { isLoading }
    } = useStore();
    // state
    const [api, setApi] = useState<CarouselApi>();

    // life cycle
    useEffect(() => {
        getMyAccount();
    }, []);

    useEffect(() => {
        const findTab = MY_ACCOUNT_TABS.findIndex(
            tab => tab.pathName === tabPath
        );

        if (findTab > -1) {
            api?.scrollTo(findTab, false);
        }
        if (tabPath !== MY_ACCOUNT_PATH.rentalHistory) {
            cleanRental();
        }
    }, [tabPath, api]);

    // const
    const currentTab = MY_ACCOUNT_TABS.find(tab => tab.pathName === tabPath)?.pathName ?? (tabPath ? undefined : '');

    const onCanAccess = (tabPath: string | undefined, userRole: number | undefined): boolean => {
        const tab = MY_ACCOUNT_TABS.find(tab => tab.pathName === tabPath);
        if (!tab) return false;
        if (!tab.roles || tab.roles?.length === 0) return true;
        if (userRole === undefined) return false;
        return tab.roles.includes(userRole);
    };

    // const checkPage = currentTab === undefined || (id && currentTab !== MY_ACCOUNT_PATH.rentalHistory);
    const checkPage = useMemo((): boolean => {
        const currentTabTmp = MY_ACCOUNT_TABS.find(tab => tab.pathName === tabPath)?.pathName ?? (tabPath ? undefined : '');
        const canAccess = onCanAccess(currentTabTmp, myAccount?.role?.id);

        return currentTabTmp === undefined || (id && currentTabTmp !== MY_ACCOUNT_PATH.rentalHistory) || !canAccess;
    }, [tabPath, id, myAccount?.role?.id]);

    const handleNavigate = (path: string) => {
        const redirectPath = isPathEk(pathname) ?
            `/${ekDomain}/${ROUTES.myAccount.hrefEK}${path ? '/' + path : ''}` :
            `${ROUTES.myAccount.href}${path ? '/' + path : ''}`;
        navigate(redirectPath);
    };

    return (
        <>
            {checkPage ?
                (
                    <div className='h-full flex-1 flex justify-center items-center'>
                        {isLoading ? <></> : <NotFoundPage />}
                    </div>
                ) :
                (
                    <div className='flex-1'>
                        <Tabs value={currentTab} className='w-full'>
                            {!id && (
                                <TabsList className='w-full'>
                                    <Carousel
                                        opts={{
                                            align: 'start'
                                        }}
                                        className='w-full'
                                        setApi={setApi}
                                    >
                                        <CarouselContent className='w-full snap-x snap-mandatory ml-0 gap-1.5'>
                                            {MY_ACCOUNT_TABS
                                                .filter(tab => tab.roles.length === 0 || isRoles(tab.roles))
                                                .map((tab, index) => (
                                                    <CarouselItem
                                                        key={index}
                                                        className='basis-auto pl-0 h-12'
                                                    >
                                                        <TabsTriggerAcc
                                                            key={tab.pathName}
                                                            value={tab.pathName}
                                                            onClick={() => handleNavigate(tab.pathName)}
                                                            className='flex gap-2 justify-center items-center'
                                                        >
                                                            {tab.icon}
                                                            <span className={cn(
                                                                currentTab === tab.pathName ? 'inline-block' : 'hidden point-600:inline-block'
                                                            )}
                                                            >
                                                                {t(tab.label)}
                                                            </span>
                                                        </TabsTriggerAcc>
                                                    </CarouselItem>
                                                ))}
                                        </CarouselContent>
                                    </Carousel>
                                </TabsList>
                            )}
                            {MY_ACCOUNT_TABS.map(tab => (
                                <TabsContent
                                    key={tab.pathName}
                                    value={tab.pathName}
                                    className='mt-0'
                                >
                                    {tab.content}
                                </TabsContent>
                            ))}
                        </Tabs>
                    </div>
                )}
        </>
    );
});

const StartIcon = ({ className = '', width = '18', height = '18' }) => {
    return (
        <div className={cn(className)}>
            <svg
                width={width}
                height={height}
                viewBox='0 0 23 22'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
            >
                <path
                    d='M1.77701 8.15104C1.5843 8.15104 1.41764 8.27604 1.35514 8.45833C1.29785 8.64063 1.36555 8.83854 1.5218 8.95312L7.0843 12.9479L4.94368 19.4531C4.88118 19.6354 4.94889 19.8385 5.10514 19.9531C5.25618 20.0677 5.46972 20.0625 5.62597 19.9531L11.152 15.901L16.6781 19.9531C16.8343 20.0677 17.0426 20.0625 17.1989 19.9531C17.3551 19.8385 17.4176 19.6354 17.3603 19.4531L15.2145 12.9479L20.7822 8.95312C20.9385 8.83854 21.0062 8.64063 20.9489 8.45833C20.8864 8.27604 20.7145 8.15104 20.527 8.15104H13.6624L11.5739 1.64062C11.4541 1.27604 10.8499 1.27604 10.7301 1.64062L8.6416 8.15104H1.77701ZM16.9385 21.3698C16.5687 21.3698 16.1989 21.2552 15.8864 21.026L11.152 17.5573L6.41764 21.026C5.78743 21.4844 4.94889 21.4844 4.31868 21.0312C3.69368 20.5729 3.43326 19.7708 3.67805 19.0365L5.5166 13.4583L0.740554 10.0365C0.115554 9.58333 -0.150071 8.78646 0.0843037 8.04688C0.323886 7.3125 1.00618 6.81771 1.77701 6.81771H7.66764L9.4593 1.23438C9.69889 0.494792 10.376 0 11.152 0C11.9228 0 12.6051 0.494792 12.8447 1.23438L14.6364 6.81771H20.527C21.2978 6.81771 21.9749 7.3125 22.2145 8.04688C22.4541 8.78646 22.1885 9.58333 21.5583 10.0365L16.7874 13.4583L18.626 19.0365C18.8656 19.776 18.6103 20.5729 17.9801 21.0312C17.6676 21.2552 17.3031 21.3698 16.9385 21.3698Z'
                    fill='currentColor'
                />
            </svg>
        </div>
    );
};

const HistoryIcon = ({ className = '', width = '18', height = '18' }) => {
    return (
        <div className={cn(className)}>
            <svg
                width={width}
                height={height}
                viewBox='0 0 25 23'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
            >
                <path
                    d='M13.0387 22.9115C10.0804 22.9115 7.29915 21.7605 5.21061 19.6667C3.12207 17.5782 1.96582 14.797 1.97103 11.8386C1.97103 10.5938 2.17936 9.35946 2.59082 8.18237L2.68978 7.89591L0.99707 6.83862C0.944986 6.80737 0.918945 6.7605 0.918945 6.72925L6.41374 3.45841L6.55957 3.47404C6.61686 3.5105 6.6429 3.56779 6.6377 3.59904L6.15853 9.94279L5.99186 9.95321L4.43978 8.98446L4.30436 9.47404C4.09603 10.2449 3.99186 11.0417 3.98665 11.8386C3.99186 14.2605 4.92936 16.5313 6.6429 18.2397C8.35124 19.948 10.6273 20.8907 13.0387 20.8907C15.4554 20.8907 17.7314 19.948 19.4398 18.2397C21.1481 16.5313 22.0908 14.2553 22.0908 11.8386C22.0908 9.42716 21.1481 7.15112 19.4398 5.44279C17.82 3.823 15.695 2.89071 13.4189 2.79696V0.776123C16.2367 0.869874 18.8721 2.0105 20.8721 4.0105C22.9606 6.10425 24.1117 8.88029 24.1117 11.8386C24.1117 14.797 22.9606 17.5782 20.8721 19.6667C18.7783 21.7605 16.0023 22.9115 13.0387 22.9115Z'
                    fill='currentColor'
                />
                <path
                    d='M9.16908 10.1823L11.5545 12.026C11.6482 12.7656 12.2732 13.3437 13.0389 13.3437C13.8722 13.3437 14.5441 12.6718 14.5441 11.8385C14.5441 11.8073 14.5389 11.776 14.5337 11.7448L18.0337 7.60934C18.2732 7.3385 18.2785 6.94267 18.0441 6.72913C17.8045 6.51559 17.4139 6.56767 17.1691 6.84371L13.7368 10.5156C13.5232 10.4062 13.2941 10.3385 13.0389 10.3385C12.7732 10.3385 12.5232 10.4114 12.3045 10.5364L10.0597 8.97392C9.76804 8.76038 9.33575 8.85934 9.09096 9.19267C8.84616 9.526 8.87741 9.96871 9.16908 10.1823Z'
                    fill='currentColor'
                />
            </svg>
        </div>
    );
};

export const MY_ACCOUNT_TABS = [
    {
        label: 'words_title.my_account',
        icon: <UserRound />,
        content: <MyAccountTab />,
        pathName: MY_ACCOUNT_PATH.myAccount,
        roles: [] // All roles
    },
    {
        label: 'words_title.favorite_location',
        icon: <StartIcon />,
        content: <RegistrationLocationTab />,
        pathName: MY_ACCOUNT_PATH.registrationLocation,
        roles: ENDUSER_ROLES
    },
    {
        label: 'words_title.rental_history',
        icon: <HistoryIcon />,
        content: <RentalHistoryTab />,
        pathName: MY_ACCOUNT_PATH.rentalHistory,
        roles: ENDUSER_ROLES
    }
];
